import React from 'react';
import {useTranslation} from 'react-i18next';
import {PlaceListing} from '../../admin/artisticobjects/types/Place';
import PlaceItem from './PlaceItem';
import {getThumbnailUrl} from '../../common/utils/imageUtils';

interface PlacesListingContentProps {
  places: PlaceListing[];
  pagination?: React.ReactNode;
  zoomedIn: boolean;
  withDescriptions: boolean;
  columnsCount: number;
  skipWrapper?: boolean;
  skipPagination?: boolean;
}

interface GridColumns {
  xl: number;
  lg: number;
  md: number;
  default: number;
}

interface GridConfig {
  [columnsCount: number]: GridColumns;
}

const GRID_SIZES: GridConfig = {
  8: {xl: 8, lg: 6, md: 4, default: 2},
  6: {xl: 6, lg: 4, md: 2, default: 1},
  4: {xl: 4, lg: 4, md: 2, default: 1},
  2: {xl: 2, lg: 2, md: 1, default: 1},
  1: {xl: 1, lg: 1, md: 1, default: 1},
}

const PlacesListingContent: React.FC<PlacesListingContentProps> = ({
  places,
  pagination,
  zoomedIn,
  withDescriptions,
  columnsCount,
  skipWrapper = false,
  skipPagination = false
}) => {
  const { t } = useTranslation();
  const gridSize = GRID_SIZES[columnsCount] || GRID_SIZES[4];

  const content = (
    <>
      <div className="hidden grid
        grid-cols-1 grid-cols-2 grid-cols-4 grid-cols-6 grid-cols-8
        md:grid-cols-1 md:grid-cols-2 md:grid-cols-4 md:grid-cols-6 md:grid-cols-8
        lg:grid-cols-1 lg:grid-cols-2 lg:grid-cols-4 lg:grid-cols-6 lg:grid-cols-8
        xl:grid-cols-1 xl:grid-cols-2 xl:grid-cols-4 xl:grid-cols-6 xl:grid-cols-8"/>
      <div className={`gap-6 grid
          grid-cols-${gridSize.default}
          md:grid-cols-${gridSize.md}
          lg:grid-cols-${gridSize.lg}
          xl:grid-cols-${gridSize.xl}`
      }>
        {places.map((place) => {
          const firstImage = place.firstImage;
          const imageSrc = firstImage ? getThumbnailUrl(firstImage) : '/images/placeholder.jpg';
          const imageAlt = firstImage?.originalName || place.name;
          const aoCount = place.artisticObjectCount ?? 0;
          const caption = withDescriptions
            ? t('locations.artisticObjectCount', { count: aoCount })
            : place.name;

          return (
            <PlaceItem
              key={place.id}
              title={place.name}
              organizationalUnit={place.churchUnit?.name || t('gallery.item.unknown')}
              image={{
                src: imageSrc,
                alt: imageAlt,
                focalPoint: firstImage?.focalPoint,
                size: firstImage ? {width: firstImage.width, height: firstImage.height} : undefined
              }}
              caption={caption}
              town={place.address?.town || t('gallery.item.unknown')}
              zoomedIn={zoomedIn}
              withDescriptions={withDescriptions}
              placeId={place.id}
            />
          );
        })}
      </div>

      {!skipPagination && pagination}
    </>
  );

  if (places.length === 0) {
    return (
      <section id="places-listing" className="bg-gray-100 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-20">
            <div className="text-lg text-gray-600">{t('places.listing.noPlacesFound')}</div>
          </div>
        </div>
      </section>
    );
  }

  if (skipWrapper) {
    return content;
  }

  return (
    <section id="places-listing" className="bg-gray-100 py-8">
      <div className="container mx-auto px-4">
        {content}
      </div>
    </section>
  );
};

export default PlacesListingContent;

